import { motion } from "framer-motion";
import { TrendingUp, Rocket, Shield, Monitor, CheckCircle2, GraduationCap, Globe } from "lucide-react";
import { focusAreas } from "@/data";

const iconMap: Record<string, React.ElementType> = {
  TrendingUp, Rocket, Shield, Monitor,
};

const colors = [
  "from-blue-700 to-blue-900",
  "from-violet-700 to-violet-900",
  "from-teal-700 to-teal-900",
  "from-indigo-700 to-indigo-900",
];

export default function FocusAreas() {
  return (
    <div className="py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Strategic Focus</p>
          <h1 className="text-4xl font-bold text-foreground mb-3">Key Focus Areas</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Beyond the core pillars, GBSRC's framework targets four strategic domains that define the graduate profile and institutional purpose.
          </p>
        </div>

        {/* Focus area cards */}
        <div className="grid sm:grid-cols-2 gap-6 mb-14">
          {focusAreas.map((f, i) => {
            const Icon = iconMap[f.icon] ?? TrendingUp;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-background border border-border rounded-2xl p-6 hover:border-primary/40 hover:shadow-lg transition-all"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[i]} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-lg font-bold text-foreground mb-4">{f.title}</h2>
                <ul className="space-y-2">
                  {f.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground/75">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Faculty section */}
        <div className="bg-primary rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-3">Faculty as Learning Architects</h2>
              <p className="text-white/80 leading-relaxed mb-4">
                The faculty role at GBSRC extends well beyond content delivery. Faculty members function as learning designers, industry connectors, mentors, and researchers — building a living, adaptive learning ecosystem.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Facilitators guiding inquiry-based learning rather than delivering monologue",
                  "Mentors and coaches accompanying students through academic and professional challenges",
                  "Researchers contributing to applied knowledge in management and allied disciplines",
                  "Industry connectors bridging classroom learning and corporate practice",
                  "Continuous professional development in AI, case writing, and innovative pedagogy",
                ].map((pt) => (
                  <div key={pt} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-white/60 shrink-0 mt-0.5" />
                    <p className="text-sm text-white/80">{pt}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Global orientation */}
        <div className="bg-muted/40 border border-border rounded-2xl p-8">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-700 to-sky-900 flex items-center justify-center shrink-0">
              <Globe className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground mb-3">Global Orientation</h2>
              <p className="text-foreground/70 leading-relaxed mb-4">
                Business in the twenty-first century is irreversibly global. GBSRC prepares students to navigate diverse business cultures, international market dynamics, and the complexities of digital globalisation.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "International business perspectives across core and elective courses",
                  "Cross-cultural management competencies for diverse leadership environments",
                  "Global and Indian case studies offering comparative strategic analysis",
                  "Emerging market analysis developing competency in high-growth contexts",
                  "Digital globalisation awareness: platform economies, digital trade, and fintech",
                ].map((pt) => (
                  <div key={pt} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground/80">{pt}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Graduate Profile */}
        <div className="mt-10 text-center">
          <h2 className="text-xl font-bold text-foreground mb-6">The GBSRC Graduate Profile</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              "Analytically Sharp",
              "Ethically Grounded",
              "Digitally Fluent",
              "Globally Competitive",
              "Entrepreneurially Minded",
              "Sustainability-Aware",
              "Lifelong Learner",
            ].map((trait) => (
              <span
                key={trait}
                className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded-full"
              >
                {trait}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
